import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Phone, 
  Mail, 
  MapPin, 
  Award, 
  Calendar,
  Users,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  Share2,
  ExternalLink,
  Grid,
  List,
  Filter
} from 'lucide-react';
import { getProjects, getCompanyInfo } from '../data/api';
import { useWhatsApp } from '../hooks/useWhatsApp';

// Certification data
const certifications = [
  {
    name: 'BCA Registered Contractor',
    logo: 'https://static.prod-images.emergentagent.com/jobs/4d5b8ed4-2ac3-4ec7-9c48-fbf59b7e6cc9/images/9287fa85510296ad77bcccf162db03374040bcadc299fddae0bdf4f48f88d90a.png'
  },
  {
    name: 'bizSAFE STAR',
    logo: 'https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/97ulnv35_BIZSAFE.png'
  },
  {
    name: 'ISO 9001:2015',
    logo: 'https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/hvr1oriq_SS506-SAC-400x400.png'
  },
  {
    name: 'EQAIMS',
    logo: 'https://static.prod-images.emergentagent.com/jobs/4d5b8ed4-2ac3-4ec7-9c48-fbf59b7e6cc9/images/d4230ce170c455705871b401180e24e4406af1a6851b765c8c89ffe2b9ae17af.png'
  }
];

const DigitalPortfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filterCategory, setFilterCategory] = useState('all');
  const portfolioRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [companyInfo, setCompanyInfo] = useState({ name: '', phone: '', email: '', address: '', projectsCompleted: '', happyClients: '' });
  const { openChat } = useWhatsApp();

  useEffect(() => {
    getProjects().then(setProjects).catch(() => {});
    getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  // Get unique categories
  const categories = ['all', ...new Set(projects.map(p => p.category))];

  // Filter projects
  const filteredProjects = filterCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === filterCategory);

  // Group projects by category for organized display
  const projectsByCategory = {
    hospitality: filteredProjects.filter(p => p.category === 'hospitality'),
    retail: filteredProjects.filter(p => p.category === 'retail'),
    office: filteredProjects.filter(p => p.category === 'office'),
    residential: filteredProjects.filter(p => p.category === 'residential'),
    industrial: filteredProjects.filter(p => p.category === 'industrial')
  };

  const categoryLabels = {
    hospitality: 'F&B / Hospitality',
    retail: 'Retail',
    office: 'Office / Commercial',
    residential: 'Residential',
    industrial: 'Industrial'
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-white" ref={portfolioRef}>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* Header / Cover Page */}
      <header className="bg-gradient-to-br from-[#1E3A5F] to-[#2D5A87] text-white min-h-[60vh] flex flex-col justify-center relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 text-center">
          {/* Logo */}
          <div className="mb-8">
            <div className="w-32 h-32 mx-auto bg-white rounded-2xl shadow-2xl flex items-center justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/6ud4osh2_logolarge.png"
                alt="Yusen Design Logo"
                className="w-24 h-24 object-contain"
              />
            </div>
          </div>

          {/* Company Name */}
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            {companyInfo.name}
          </h1>
          
          {/* Tagline */}
          <p className="text-xl md:text-2xl text-blue-200 mb-8">
            Interior Design & Renovation Company Singapore
          </p>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 mb-12">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold">{companyInfo.projectsCompleted}</p>
              <p className="text-blue-200 text-sm">Projects Completed</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold">38+</p>
              <p className="text-blue-200 text-sm">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold">{companyInfo.happyClients}</p>
              <p className="text-blue-200 text-sm">Happy Clients</p>
            </div>
          </div>

          {/* Action Buttons - No Print */}
          <div className="flex flex-wrap justify-center gap-4 no-print">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 bg-white text-[#1E3A5F] px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
            <Link
              to="/share"
              className="inline-flex items-center gap-2 bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors"
            >
              <Share2 className="w-5 h-5" />
              Share Portfolio
            </Link>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </header>

      {/* Company Overview Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* About Text */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">About Us</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                {companyInfo.description}
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Established in {companyInfo.established} and incorporated in {companyInfo.incorporated}, 
                we have grown to become one of Singapore's most trusted interior design and renovation companies. 
                Our commitment to quality craftsmanship and customer satisfaction has earned us a reputation 
                for excellence in the industry.
              </p>

              {/* Key Points */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium">BCA Registered</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium">Expert Team</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium">Turnkey Solutions</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium">On-Time Delivery</span>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Address</p>
                    <p className="text-gray-600">{companyInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Phone</p>
                    <p className="text-gray-600">{companyInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Email</p>
                    <p className="text-gray-600">{companyInfo.email}</p>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="font-medium text-gray-900 mb-2">Office Hours</p>
                <p className="text-sm text-gray-600">{companyInfo.officeHours?.weekdays}</p>
                <p className="text-sm text-gray-600">{companyInfo.officeHours?.saturday}</p>
              </div>

              {/* Registration */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-xs text-gray-500">Company Reg. No.: {companyInfo.regNo}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-12 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Our Certifications</h2>
          <div className="flex flex-wrap justify-center gap-8">
            {certifications.map((cert) => (
              <div key={cert.name} className="flex flex-col items-center">
                <div className="w-24 h-24 bg-white rounded-xl shadow-md flex items-center justify-center p-4 mb-3">
                  <img 
                    src={cert.logo} 
                    alt={cert.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-xs text-gray-600 text-center max-w-[100px]">{cert.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter & View Controls - No Print */}
      <section className="py-8 px-6 bg-white border-b sticky top-0 z-40 no-print">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Category Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto">
              <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`portfolio-filter-${cat}`}
                  onClick={() => setFilterCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    filterCategory === cat
                      ? 'bg-[#1E3A5F] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All Projects' : categoryLabels[cat] || cat}
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-[#1E3A5F] text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list' ? 'bg-[#1E3A5F] text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Portfolio */}
      <section className="py-12 px-6 bg-white" data-testid="portfolio-projects-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Our Portfolio</h2>
          <p className="text-gray-600 text-center mb-12">
            Showcasing {filteredProjects.length} completed projects across Singapore
          </p>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div data-testid="portfolio-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100"
                  onClick={() => {
                    setSelectedProject(project);
                    setSelectedImage(0);
                  }}
                >
                  {/* Project Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={project.galleryImages?.[0] || project.beforeImage}
                      alt={`${project.title} - Interior Design Project Singapore`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="inline-block bg-white/90 text-gray-900 text-xs font-medium px-2 py-1 rounded">
                        View Project
                      </span>
                    </div>
                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#1E3A5F]/90 text-white text-xs font-medium px-2 py-1 rounded">
                        {categoryLabels[project.category] || project.category}
                      </span>
                    </div>
                    {/* Image Count */}
                    {project.galleryImages && project.galleryImages.length > 1 && (
                      <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        {project.galleryImages.length} photos
                      </div>
                    )}
                  </div>

                  {/* Project Info */}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1 group-hover:text-blue-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {project.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="space-y-6">
              {filteredProjects.map((project, index) => (
                <div
                  key={project.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 flex flex-col md:flex-row"
                  onClick={() => {
                    setSelectedProject(project);
                    setSelectedImage(0);
                  }}
                >
                  {/* Project Image */}
                  <div className="relative w-full md:w-72 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={project.galleryImages?.[0] || project.beforeImage}
                      alt={`${project.title} - Interior Design Project Singapore`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-[#1E3A5F]/90 text-white text-xs font-medium px-2 py-1 rounded">
                        {categoryLabels[project.category] || project.category}
                      </span>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div className="p-6 flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mb-3">
                      <MapPin className="w-4 h-4" />
                      {project.location}
                    </p>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {project.description}
                    </p>
                    {project.features && (
                      <div className="flex flex-wrap gap-2">
                        {project.features.slice(0, 3).map((feature) => (
                          <span key={feature} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 no-print">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedProject.title}</h3>
                <p className="text-sm text-gray-500">{selectedProject.location}</p>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Main Image */}
            <div className="relative h-[50vh] bg-gray-100">
              <img
                src={selectedProject.galleryImages?.[selectedImage] || selectedProject.beforeImage}
                alt={selectedProject.title}
                className="w-full h-full object-contain"
              />
              
              {/* Navigation Arrows */}
              {selectedProject.galleryImages && selectedProject.galleryImages.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(prev => 
                        prev === 0 ? selectedProject.galleryImages.length - 1 : prev - 1
                      );
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(prev => 
                        prev === selectedProject.galleryImages.length - 1 ? 0 : prev + 1
                      );
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Image Counter */}
              {selectedProject.galleryImages && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                  {selectedImage + 1} / {selectedProject.galleryImages.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {selectedProject.galleryImages && selectedProject.galleryImages.length > 1 && (
              <div className="p-4 border-t bg-gray-50">
                <div className="flex gap-2 overflow-x-auto">
                  {selectedProject.galleryImages.map((img, index) => (
                    <button
                      key={`thumb-${img.slice(-20)}`}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Project Details */}
            <div className="p-4 border-t max-h-40 overflow-y-auto">
              <p className="text-gray-600 text-sm mb-3">{selectedProject.description}</p>
              {selectedProject.features && (
                <div className="flex flex-wrap gap-2">
                  {selectedProject.features.map((feature) => (
                    <span key={feature} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">
                      {feature}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#1E3A5F] to-[#2D5A87] text-white no-print">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            Get a free consultation and quotation from our expert team
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => openChat({ page: 'portfolio', context: 'cta-section' })}
              className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-xl font-semibold transition-colors"
            >
              WhatsApp Us Now
            </button>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1E3A5F] px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400 mb-2">{companyInfo.name}</p>
          <p className="text-gray-500 text-sm">
            Interior Design and Renovation Company Singapore | BCA Registered
          </p>
          <p className="text-gray-600 text-xs mt-4">
            © 2025 All Rights Reserved | {companyInfo.address}
          </p>
        </div>
      </footer>

      {/* Back to Home - Fixed Button */}
      <Link
        to="/"
        className="fixed bottom-6 left-6 bg-white shadow-xl rounded-full p-3 hover:shadow-2xl transition-shadow no-print z-40"
      >
        <ExternalLink className="w-5 h-5 text-gray-700" />
      </Link>
    </div>
  );
};

export default DigitalPortfolio;
